import { SummaryCard } from '../../../types';

export const fenCards: SummaryCard[] = [
  {
    id: 'fn-001',
    title: 'Mevsimler ve İklim',
    subject: 'fen',
    topic: 'Dünya ve Evren',
    content: `# Mevsimler ve İklim

## Neden Mevsimler Oluşur?
- Dünya'nın eksen eğikliği (23,5°) ve Güneş etrafındaki dolanımı nedeniyle yıl boyunca güneş ışınlarının geliş açısı ve gün uzunlukları değişir.
- Işınlar dik geldikçe ısınma artar, gölge boyu kısalır; eğik geldikçe ısınma azalır, gölge boyu uzar.

## Önemli Tarihler (Kuzey Yarım Küre)
- 21 Mart: İlkbahar başlar, gece–gündüz eşit.
- 21 Haziran: Yaz başlar, gündüzler en uzun.
- 23 Eylül: Sonbahar başlar, gece–gündüz eşit.
- 21 Aralık: Kış başlar, geceler en uzun.

## İklim ve Hava Durumu
- Hava durumu: Kısa süreli, bölgesel atmosfer olayları.
- İklim: Uzun yıllar boyunca gözlenen ortalama koşullar.
- Türkiye: Karadeniz (yıl boyu yağışlı), Akdeniz (yazlar kurak–sıcak), Karasal (kışlar soğuk, yazlar sıcak–kurak).

## Günlük Yaşama Etkileri
- Tarım takvimi, turizm sezonu ve enerji tüketimi mevsimlere bağlı değişir.`,
    keyPoints: [
      'Mevsimler eksen eğikliği ve dolanımın sonucudur',
      'Güneş ışınları dik geldikçe sıcaklık artar, gölge kısalır',
      '21 Mart/23 Eylül ekinoks, 21 Haziran/21 Aralık gündüz–gece uçlarıdır',
      'Hava durumu kısa sürelidir, iklim uzun yılların ortalamasıdır',
      'Türkiye’de 3 ana iklim: Karadeniz, Akdeniz, Karasal'
    ],
    examples: [
      'Yazın öğle vakti gölgelerin kısalması (ışınlar daha dik)',
      'Ankara’da kışlar soğuk, yazlar sıcak–kurak: karasal iklim',
      'Karadeniz’de her mevsim yağış görülmesi'
    ],
    difficulty: 'easy',
    estimatedReadTime: 6,
    tags: ['mevsim', 'iklim', 'hava durumu', 'eksen eğikliği', 'güneş açısı'],
    color: '#10B981',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'fn-002',
    title: 'DNA ve Genetik Kod',
    subject: 'fen',
    topic: 'Canlılar ve Yaşam',
    content: `# DNA ve Genetik Kod

## DNA'nın Yapısı ve Görevi
- Çift sarmal; nükleotitlerden (şeker, fosfat, baz) oluşur.
- Baz eşleşmeleri: Adenin–Timin (A–T), Guanin–Sitozin (G–C). Sıralama protein bilgisidir.

## Gen–Kromozom–Nükleotit
- Gen: Belirli bir özelliği kodlayan DNA parçası.
- Kromozom: DNA + protein; insan vücut hücresinde 46 kromozom bulunur.
- Nükleotit: DNA’nın yapı taşıdır.

## Protein Sentezi (Özet)
- DNA’dan mRNA kopyalanır (çekirdek) → mRNA ribozoma gider → aminoasitler sırayla bağlanır → protein oluşur.

## Kalıtım Temelleri
- Genotip (AA, Aa, aa) fenotipi (gözlenen özellik) belirler.
- Baskın (A) fenotipte görünür; çekinik (a) yalnızca aa iken görünür.`,
    keyPoints: [
      'DNA çift sarmal yapıda kalıtsal bilgiyi taşır',
      'Baz eşleşmesi A–T ve G–C kuralına uyar',
      'Gen bir özelliğin bilgisini, kromozom DNA-protein kompleksini ifade eder',
      'Protein sentezi: DNA → mRNA → protein',
      'Baskın–çekinik ilişki fenotipi belirler'
    ],
    examples: [
      'Kahverengi göz (A) baskın, mavi göz (a) çekinik olabilir',
      'Aa × aa çaprazlamasında çocukların %50’si aa (çekinik fenotip) olabilir',
      'Genotip değişirse fenotip de değişebilir'
    ],
    difficulty: 'medium',
    estimatedReadTime: 7,
    tags: ['DNA', 'gen', 'kromozom', 'protein sentezi', 'kalıtım'],
    color: '#10B981',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'fn-003',
    title: 'Basınç',
    subject: 'fen',
    topic: 'Fiziksel Olaylar',
    content: `# Basınç

## Katı Basıncı
- Basınç (P) = Kuvvet (F) / Yüzey Alanı (A). Alan küçüldükçe basınç artar.

## Sıvı Basıncı
- Derinlikle artar: P = h · d · g (h: yükseklik, d: yoğunluk, g: yerçekimi).
- Aynı derinlikte aynı cins sıvıların basıncı eşittir; kap şekli önemli değildir.

## Gaz Basıncı
- Gazlar kabın her noktasına basınç uygular. Sıcaklık artışı basıncı artırabilir (kapalı kapta).

## Birimler ve Uygulamalar
- Birim: Pascal (Pa). 1 Pa = 1 N/m².
- Keskin bıçak, kar ayakkabısı, çivinin sivri ucu basınç etkisine örnektir.`,
    keyPoints: [
      'P = F/A; alan küçülürse basınç artar',
      'Sıvı basıncı derinlik ve yoğunlukla doğru orantılıdır',
      'Kap şekli sıvı basıncını değiştirmez, derinlik belirleyicidir',
      'Gazlar kabın her noktasına basınç uygular',
      'Birim Pascal (Pa) olarak ifade edilir'
    ],
    formulas: [
      'P = F / A',
      'P(sıvı) = h · d · g'
    ],
    examples: [
      '300 N kuvveti 0,01 m² alana uygularsan: P = 300/0,01 = 30.000 Pa',
      '10 m su derinliğinde: P ≈ 10·1000·9,8 = 98.000 Pa (yaklaşık 98 kPa)'
    ],
    difficulty: 'medium',
    estimatedReadTime: 6,
    tags: ['basınç', 'sıvı basıncı', 'gaz basıncı', 'F/A', 'hdg', 'Pa'],
    color: '#10B981',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'fn-004',
    title: 'Madde ve Endüstri',
    subject: 'fen',
    topic: 'Madde ve Değişim',
    content: `# Madde ve Endüstri

## Saf Madde ve Karışımlar
- Saf madde: Tek tür tanecik (element, bileşik). Karışım: Birden fazla madde fiziksel yollarla bir arada (homojen/heterojen).

## Ayırma Yöntemleri (Özet)
- Heterojen karışımlar: Süzme, çöktürme, mıknatısla ayırma.
- Homojen çözeltiler: Buharlaştırma (tuz–su), damıtma (alkol–su), kromatografi (mürekkep bileşenleri).

## Endüstride Kimya ve Çevre
- Polimerler, deterjanlar, gübreler, ilaç ve boya endüstrisi yaygındır.
- Atık yönetimi, geri dönüşüm ve arıtma çevre sağlığı için kritiktir.`,
    keyPoints: [
      'Saf madde element/bileşik; karışımlar fiziksel yollarla ayrılır',
      'Yöntem karışımın türüne göre seçilir',
      'Buharlaştırma ve damıtma çözeltileri ayırmada kullanılır',
      'Endüstride verim–çevre dengesi önemlidir'
    ],
    examples: [
      'Tuzlu suyu buharlaştırma ile ayırma',
      'Demir tozunu mıknatısla ayırma',
      'Mürekkep renklerini kromatografi ile ayırma'
    ],
    difficulty: 'easy',
    estimatedReadTime: 5,
    tags: ['madde', 'karışım', 'ayırma', 'buharlaştırma', 'damıtma', 'kromatografi', 'geri dönüşüm'],
    color: '#10B981',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'fn-005',
    title: 'Basit Makineler',
    subject: 'fen',
    topic: 'Fiziksel Olaylar',
    content: `# Basit Makineler

## Amaç ve İlke
- İş yapmayı kolaylaştırır; kuvvetten veya yoldan kazanç sağlar. Sürtünme yoksa toplam iş değişmez.

## Türler ve Özet Örnekler
- Kaldıraç: Makas, pense (kol uzunluklarıyla MA artar).
- Makaralar: Sabit (yön değiştirir), hareketli (kuvvetten kazanç sağlar).
- Eğik Düzlem: Daha uzun yoldan daha az kuvvetle iş yapma.
- Vida/Çıkrık: Döndürme hareketiyle kuvvet kazancı.
- Dişliler: Dönme yönü ve hız değişimi.

## Mekanik Avantaj (MA)
- Kaldıraç için: MA = Kuvvet Kolu / Yük Kolu.`,
    keyPoints: [
      'Basit makineler iş yapış şeklini değiştirir, toplam iş korunur',
      'Kaldıraçlarda kol oranı MA’yı belirler',
      'Hareketli makara kuvvetten kazanç sağlar',
      'Eğik düzlem: daha uzun yol, daha az kuvvet'
    ],
    formulas: [
      'MA = Y / F',
      'MA(kaldıraç) = Kuvvet Kolu / Yük Kolu'
    ],
    examples: [
      'Makas (kaldıraç) ile kesme kuvvetinin artırılması',
      'İnşaatta makara sistemiyle yük kaldırma',
      'Rampa ile arabayı daha az kuvvetle yukarı itme'
    ],
    difficulty: 'medium',
    estimatedReadTime: 6,
    tags: ['basit makineler', 'kaldıraç', 'makara', 'eğik düzlem', 'mekanik avantaj'],
    color: '#10B981',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'fn-006',
    title: 'Enerji Dönüşümleri ve Çevre Bilimi',
    subject: 'fen',
    topic: 'Enerji ve Çevre',
    content: `# Enerji Dönüşümleri ve Çevre Bilimi

## Enerji Türleri ve Dönüşüm
- Potansiyel, kinetik, elektrik, ısı, kimyasal, ışık enerjileri birbirine dönüşebilir.
- Enerjinin Korunumu: Kapalı sistemlerde toplam enerji sabittir.

## Verim ve Sürdürülebilirlik
- Verim = Faydalı Enerji / Toplam Enerji. Kayıplar çoğunlukla ısı olarak ortaya çıkar.
- Yenilenebilir kaynaklar (güneş, rüzgar, hidroelektrik, jeotermal, biyokütle) çevresel etkileri azaltır.

## Çevresel Etkiler
- Fosil yakıtlar sera gazı emisyonu ve hava kirliliği oluşturur; verimlilik ve geri dönüşüm önemlidir.`,
    keyPoints: [
      'Enerji türleri dönüşebilir, toplam enerji korunur',
      'Verim arttıkça enerji israfı azalır',
      'Yenilenebilir enerji çevre etkilerini düşürür',
      'Fosil yakıtlar sera gazı ve kirlilik oluşturur'
    ],
    formulas: [
      'E(k) = 1/2 · m · v^2',
      'E(p) = m · g · h'
    ],
    examples: [
      'Baraj: potansiyel → kinetik → elektrik enerjisi dönüşümü',
      'Güneş paneli: ışık → elektrik enerjisi',
      'LED ampullerle verimi artırıp tüketimi azaltma'
    ],
    difficulty: 'easy',
    estimatedReadTime: 6,
    tags: ['enerji', 'dönüşüm', 'verim', 'yenilenebilir', 'sürdürülebilirlik', 'çevre'],
    color: '#10B981',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'fn-007',
    title: 'Elektrik Yükleri ve Elektrik Enerjisi',
    subject: 'fen',
    topic: 'Elektrik ve Manyetizma',
    content: `# Elektrik Yükleri ve Elektrik Enerjisi

## Yük Türleri ve Elektriklenme
- Pozitif (+) ve negatif (−) yükler; aynı yükler itişir, zıt yükler çekişir.
- Elektriklenme: Sürtünme, dokunma, etki ile gerçekleşir.

## Devre Büyüklükleri
- Akım (I): Birim zamanda geçen yük miktarı (A).
- Gerilim (V): Yükleri hareket ettiren potansiyel fark (V).
- Direnç (R): Akıma karşı gösterilen zorluk (Ω).

## Temel İlişkiler
- Ohm Kanunu: V = I · R
- Güç: P = V · I = I^2 · R = V^2 / R

## Pratik Notlar
- Seri devrede akım aynı, paralelde gerilim aynıdır.
- Güvenlik: Sigortalar aşırı akımı keser; ıslak elle prize dokunma tehlikelidir.`,
    keyPoints: [
      'Aynı yükler itişir, zıt yükler çekişir',
      'Ohm kanunu: V = I·R',
      'Elektriksel güç: P = V·I',
      'Seri akım aynı, paralelde gerilim aynıdır'
    ],
    formulas: [
      'V = I · R',
      'P = V · I',
      'Q = I · t'
    ],
    examples: [
      '220 V gerilimde 10 Ω direnç: I = 220/10 = 22 A (tehlikeli derecede yüksek)',
      'Paralel bağlı ampullerden biri patlasa diğeri yanmaya devam edebilir'
    ],
    difficulty: 'medium',
    estimatedReadTime: 7,
    tags: ['elektrik', 'ohm kanunu', 'akım', 'gerilim', 'güç', 'seri', 'paralel'],
    color: '#10B981',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  }
];